/**
 * DEV ONLY: sample price history, so the game view's chart can be designed and reviewed before
 * the key has the `price_history` entitlement. Opt in with `?demo-history` on the dev server; the
 * chart labels it "Sample data". Production builds never take this path.
 */
import { best, type SideBook } from "./board";
import type { PricePoint } from "./types";

export function wantsDemoHistory(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).has("demo-history");
}

/** DEV ONLY: the news-pins mockup. On with `?news`, and always alongside `?demo-history`. */
export function wantsNewsMockup(): boolean {
  const q = new URLSearchParams(window.location.search);
  return import.meta.env.DEV && (q.has("news") || q.has("demo-history"));
}

/**
 * A seeded random walk per side that ends on the side's current best price. `jumps` (unix ms)
 * add a step at those moments, so the mockup's news pins line up with visible moves.
 */
export function demoPoints(
  sides: SideBook[],
  window: { from: number; to: number },
  jumps: number[] = [],
): PricePoint[] {
  let seed = 7;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647) - 0.5;
  const n = 48;
  const out: PricePoint[] = [];
  const walk: number[] = [];
  const at = (i: number) => window.from + ((window.to - window.from) * i) / (n - 1);
  for (let i = 0; i < n; i++) {
    const jumped = jumps.filter((t) => t > at(i - 1) && t <= at(i)).length;
    walk.push((walk[i - 1] ?? 0) + rand() * 0.008 + 0.0004 + jumped * 0.03);
  }
  for (const [k, side] of sides.entries()) {
    const now = best(side)?.price;
    if (!now) continue;
    const hash = side.positionHashes?.[0] ?? side.sideKey;
    const sign = k === 0 ? 1 : -1;
    for (let i = 0; i < n; i++) {
      const drift = sign * (walk[i] - walk[n - 1]);
      const price = Math.min(0.97, Math.max(0.03, now + drift));
      out.push({ t: at(i), position_hash: hash, price });
    }
  }
  return out;
}
